import { getVocabulary } from 'components/Vocabulary';
import { compact, isNil } from 'lodash';
import { Key } from 'react';
import { BASE_SEVERITIES } from 'views/Findings/constants/severity';

import { RiskRatingKeys } from './riskScoreUtil';

export function displayTextWidth({
  text,
  font = '13px InterVariable',
  offset = 0,
}: {
  text: string;
  font?: string;
  offset?: number;
}) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context!.font = font;
  const metrics = context?.measureText(text);
  return (metrics?.width ?? 0) + offset;
}

export function getVocabularyColumnWidth(
  key: Key | undefined,
  offset: number | undefined = 60,
) {
  if (['riskRating', 'severity'].includes(key as string)) {
    const columnKeys = key === 'riskRating' ? RiskRatingKeys : BASE_SEVERITIES;
    return Math.max(
      ...columnKeys.map((text) =>
        displayTextWidth({ text: getVocabulary(text), offset }),
      ),
    );
  }
  return undefined;
}

// to compute new copy name if name already exists
// input "abc", if exists then return "abc Copy". If "abc Copy" also exists, return "abc Copy 1" and so on
export const computeDuplicateName = (value: string, values: string[]) => {
  let counter = 0;
  let newValue = value;
  while (values.includes(newValue) && counter < newValue.length) {
    newValue = compact([value, 'Copy', counter]).join(' ');
    counter++;
  }
  return newValue;
};

export const resolveVariables = (
  template: string,
  data: Record<string, string | number | null>,
) => {
  const unavailableValue = 'n/a';
  return template.replace(/\$\{([^}]+)\}/g, (full, expr) => {
    expr = expr.trim();
    try {
      if (/^[\w.]+$/.test(expr)) {
        const value = data[expr];
        return !isNil(value) ? `${value}` : unavailableValue;
      }

      const defaultMatch = expr.match(/^([\w.]+)\s*\|\s*(.+)$/);

      if (defaultMatch) {
        // NOTE: we may need to handle more use case in future when this is actually supported
        // currently all variables are required
        const key = defaultMatch[1];
        const defaultValue = (defaultMatch?.[2].trim() ??
          unavailableValue) as string;
        const value = data[key];
        return !isNil(value) ? `${value}` : defaultValue.replaceAll('"', '');
      }

      const ternaryMatch = expr.match(/^([\w.]+)\s*\?\s*(.*?)\s*:\s*(.*?)$/);

      if (ternaryMatch) {
        // NOTE: we may need to handle more use case in future when this is actually supported
        // currently all variables are required
        const ternaryExpression = parseTernaryExpression(ternaryMatch[0]);
        if (ternaryExpression) {
          const { variable, ifValue, elseValue } = ternaryExpression;
          const value = data[variable];
          if (!isNil(value)) {
            return ifValue.replaceAll(variable, `${value}`).replaceAll('"', '');
          }
          return elseValue.replaceAll('"', '');
        }

        return full;
      }
    } catch (e) {
      console.error(e, `Error resolving variable in ${full}`);
    }

    return full;
  });
};

function parseTernaryExpression(expr: string) {
  let inQuote = false;
  let quoteChar = null;
  let questionPos = -1;
  let colonPos = -1;

  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i];

    if ((ch === '"' || ch === "'") && expr[i - 1] !== '\\') {
      if (!inQuote) {
        inQuote = true;
        quoteChar = ch;
      } else if (quoteChar === ch) {
        inQuote = false;
      }
      continue;
    }

    if (inQuote) continue;

    if (questionPos === -1 && ch === '?') {
      questionPos = i;
      continue;
    }

    if (questionPos !== -1 && ch === ':') {
      colonPos = i;
      break;
    }
  }

  if (questionPos === -1 || colonPos === -1) {
    return null;
  }

  return {
    variable: expr.slice(0, questionPos).trim(),
    ifValue: expr.slice(questionPos + 1, colonPos).trim(),
    elseValue: expr.slice(colonPos + 1).trim(),
  };
}
